import React, { useState, useEffect, useRef } from 'react';
import { usePopper } from 'react-popper';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import TvsCheckBox from 'components/form/CheckBox';
import Arrow from 'components/elements/Arrow';

import themeColors from 'themes/themeColors';

const Portal = ({ children }) => {
	return createPortal(children, document.body);
};

export const SelectGroupMappingDropdown = ({
	data = [],
	selections,
	setSelections,
	setIsOpen,
	isOpen,
	width = '100%',
	height = '55px',
	direction = 'left',
}: {
	data: {
		group_id: number;
		level: number;
		name: string;
		members?: {
			group_id: number;
			level: number;
			name: string;
			members?: {
				group_id: number;
				level: number;
				name: string;
				members?: { group_id: number; name: string }[];
			}[];
		}[];
	}[];
	setSelections: (val: number[]) => void;
	selections: number[];
	setIsOpen: (val: boolean) => void;
	isOpen: boolean;
	width?: string;
	height?: string;
	direction?: string;
}) => {
	const widthRef = useRef<any>();
	const ref = useRef<any>();

	const [isHovering, setIsHovering] = useState<boolean>(false);
	const [isAllSelected, setIsAllSelected] = useState<boolean>(false);
	const [menuWidth, setMenuWidth] = useState(200);
	const [referenceElement, setReferenceElement] = useState(null);
	const [popperElement, setPopperElement] = useState(null);
	const { styles, attributes } = usePopper(referenceElement, popperElement, {
		modifiers: [{ name: 'arrow', options: { element: null } }],
	});

	useEffect(() => {
		setMenuWidth(
			widthRef?.current?.offsetWidth == 0 ? 200 : widthRef?.current?.offsetWidth,
		);
	}, [widthRef.current]);

	const handleMouseEnter = (e) => {
		setIsHovering(true);
	};

	const handleMouseLeave = () => {
		setIsHovering(false);
	};

	const addAllToSelected = () => {
		let allData = [];
		if (data?.[0]?.level === 0) {
			allData = data.map((d) => d.group_id);
		}
		if (data?.[0]?.level === 1) {
			data.forEach((d) => {
				d.members?.forEach((i) => {
					allData.push(i.group_id);
				});
			});
		}
		if (data?.[0]?.level === 2) {
			data.forEach((d) => {
				d.members?.forEach((i) => {
					i.members?.forEach((j) => {
						allData.push(j.group_id);
					});
				});
			});
		}
		if (data?.[0]?.level === 3) {
			data.forEach((d) => {
				d.members?.forEach((i) => {
					i.members?.forEach((j) => {
						j.members?.forEach((k) => {
							allData.push(k.group_id);
						})
					});
				});
			});
		}
		setSelections([...new Set([...selections].concat(allData))]);
	};

	let totalKeyCount = 0;
	if (data?.[0]?.level === 0) {
		totalKeyCount = data.length;
	}
	if (data?.[0]?.level === 1) {
		data.forEach((d) => {
			totalKeyCount += d.members?.length;
		});
	}
	if (data?.[0]?.level === 2) {
		data.forEach((d) => {
			d.members?.forEach((i) => {
				totalKeyCount += i.members?.length;
			});
		});
	}
	if (data?.[0]?.level === 3) {
		data.forEach((d) => {
			d.members?.forEach((i) => {
				i.members?.forEach((j) => {
					totalKeyCount += j.members?.length;
				})
			});
		});
	}

	useEffect(() => {
		if (selections.length === 0 || selections.length < totalKeyCount) {
			setIsAllSelected(false);
		} else if (selections.length === totalKeyCount) {
			setIsAllSelected(true);
		}
	}, [isOpen, selections]);

	const getDropdownLabel = () => {
		const numSelected = selections.length;

		if (numSelected === 1) {
			let label = '';
			switch (data?.[0]?.level) {
				case 0:
					return data.find((d) => d.group_id === selections[0])?.name;
				case 1:
					for (const i of data) {
						for (const j of i.members) {
							if (j.group_id === selections[0]) {
								label = j.name;
								break;
							}
						}
						if (label) {
							break;
						}
					}
					return label;
				case 2:
					for (const d of data) {
						for (const i of d.members) {
							for (const j of i.members) {
								if (j.group_id === selections[0]) {
									label = j.name;
									break;
								}
							}
							if (label) {
								break;
							}
						}
						if (label) {
							break;
						}
					}
					return label;
				case 3:
					for (const d of data) {
						for (const i of d.members) {
							for (const j of i.members) {
								for (const k of j.members) {
									if (k.group_id === selections[0]) {
										label = k.name;
										break;
									}
								}
								if (label) {
									break;
								}
							}
							if (label) {
								break;
							}
						}
						if (label) {
							break;
						}
					}
					return label;
			}
		}

		if (numSelected === 0) {
			return 'Select Locations';
		}

		if (numSelected === totalKeyCount) {
			return 'All Selected';
		}

		if (numSelected > 1) {
			return `${numSelected} selected`;
		}

		return 'Select Branch';
	};

	return (
		<div style={{ width }} ref={ref}>
			<div ref={setReferenceElement}>
				<div
					style={{
						width: '100%',
						display: 'flex',
						alignContent: 'space-between',
						justifyContent: 'space-between',
						alignItems: 'center',
						border:
							selections.length > 0 && selections.length !== totalKeyCount
								? '1px solid #6956ba'
								: '1px solid #999999',
						height,
						borderRadius: '8px',
						padding: '10px',
						backgroundColor: 'white',
					}}
					ref={widthRef}
					aria-label='dropdown'
					tabIndex={0}
					role='button'
					onClick={() => {
						setIsOpen(!isOpen);
					}}>
					<span
						style={{
							paddingRight: '10px',
							fontWeight: '400',
							fontSize: '16px',
							color:
								selections.length > 0 && selections.length !== totalKeyCount
									? themeColors.Gray333
									: themeColors.Gray999,
						}}>
						{getDropdownLabel()}{' '}
					</span>
					<Arrow border={2} length={9} color='#666666' rotate={45} />
				</div>
			</div>
			{isOpen && (
				<Portal>
					<div
						style={{
							position: 'fixed',
							right: 0,
							left: 0,
							bottom: 0,
							top: 0,
							opacity: 0.5,
						}}
						onMouseDownCapture={() => {
							setIsOpen(false);
						}}
					/>
					<div ref={setPopperElement} style={{...styles.popper, zIndex: 91}} {...attributes.popper}>
						<Container
							style={{
								listStyle: 'none',
								boxShadow:
									'0 4px 6px -1px rgb(0 0 0 / 0.1), 0 4px 6px -1px rgb(0 0 0 / 0.1)',
								marginTop: '7px',
								maxHeight: '300px',
								overflowY: 'scroll',
								width: menuWidth,
								borderRadius: '4px',
							}}>
							{data.length > 0 && (
								<li>
									<button
										style={{
											backgroundColor: isHovering ? '#e9edef' : 'white',
											fontWeight: isHovering ? '470' : '',
											position: 'relative',
											border: 'none',
											width: '100%',
											zIndex: '2',
											padding: '16px 8px 16px 16px',
											display: 'flex',
											alignContent: 'between',
											textAlign: 'left',
											alignItems: 'center',
											justifyContent: 'left',
											borderBottom: '1px solid #e5e3e3',
										}}
										onMouseEnter={() => handleMouseEnter('All')}
										onMouseLeave={handleMouseLeave}
										type='button'>
										<TvsCheckBox
											id='branch-dropdown-select-all-branch-check-box'
											handleCheck={(value: string, selected: boolean) => {
												setIsAllSelected(selected);
												if (selected) {
													addAllToSelected();
												} else if (!selected) {
													setSelections([]);
												}
											}}
											value='All'
											selectState={isAllSelected}
										/>
										<span style={{ fontSize: '16px', color: 'rgba(40, 50, 55, 1)' }}>
											All
										</span>
									</button>
								</li>
							)}
							{data.map((item, idx) => {
								return (
									<MenuItems
										item={item}
										selections={selections}
										setSelections={setSelections}
										idx={idx}
										parentRef={ref}
										direction={direction}
									/>
								);
							})}
						</Container>
					</div>
				</Portal>
			)}
		</div>
	);
};

const MenuItems = ({
	item,
	selections,
	setSelections,
	idx,
	parentRef,
	direction,
}: {
	item: {
		group_id: number;
		level: number;
		name: string;
		members?: {
			group_id: number;
			level: number;
			name: string;
			members?: {
				group_id: number;
				level: number;
				name: string;
				members?: { group_id: number; name: string }[];
			}[];
		}[];
	};
	setSelections: (val: number[]) => void;
	selections: number[];
	idx: number;
	parentRef: any;
	direction: string;
}) => {
	const [isHovering, setIsHovering] = useState(false);
	const [dropdown, setDropdown] = useState(false);
	const [referenceElement, setReferenceElement] = useState(null);
	const [popperElement, setPopperElement] = useState(null);
	const [open, setOpen] = useState(false);
	const [menuWidth, setMenuWidth] = useState(200);

	const { styles, attributes } = usePopper(referenceElement, popperElement, {
		modifiers: [{ name: "arrow", options: { element: null } }],
	});

	const widthRef = useRef<any>();

	useEffect(() => {
		setMenuWidth(widthRef.current ? widthRef.current.offsetWidth : 200);
	}, [widthRef.current]);

	const handleMouseEnter = (e) => {
		setIsHovering(true);
		setDropdown(true);
	};

	const handleMouseLeave = () => {
		setIsHovering(false);
		setDropdown(false);
	};

	useEffect(() => {
		setOpen(open);
	}, []);

	const inSelection = (item: number) => {
		return selections.includes(item);
	};

	const removeFromList = (item: number) => {
		return selections.filter((e) => e !== item);
	};

	const addMembersToSelected = () => {
		let allMemberData = [];
		if (item.level === 3) {
			item?.members.forEach((d) => {
				d.members?.forEach((i) => {
					i.members?.forEach((j) => {
						allMemberData.push(j.group_id);
					});
				});
			});
		}
		if (item.level === 2) {
			item?.members.forEach((d) => {
				d.members?.forEach((i) => {
					allMemberData.push(i.group_id);
				});
			});
		}
		if (item.level === 1) {
			item?.members.forEach((d) => {
				allMemberData.push(d.group_id);
			});
		}
		setSelections([...new Set([...selections].concat(allMemberData))]);
	};

	const removeMembersFromSelected = () => {
		let allMemberData = [];
		if (item.level === 3) {
			item?.members.forEach((d) => {
				d.members?.forEach((i) => {
					i.members?.forEach((j) => {
						allMemberData.push(j.group_id);
					})
				});
			});
		}
		if (item.level === 2) {
			item?.members.forEach((d) => {
				d.members?.forEach((i) => {
					allMemberData.push(i.group_id);
				});
			});
		}
		if (item.level === 1) {
			item?.members.forEach((d) => {
				allMemberData.push(d.group_id);
			});
		}
		setSelections([
			...new Set([...selections.filter((e) => !allMemberData.includes(e))]),
		]);
	};

	const checkSelectState = () => {
		let allMemberData = [];
		switch (item.level) {
			case 0:
				return inSelection(item.group_id);
			case 1:
				item?.members.forEach((d) => {
					allMemberData.push(d.group_id);
				});
				break;
			case 2:
				item?.members.forEach((d) => {
					d.members?.forEach((i) => {
						allMemberData.push(i.group_id);
					});
				});
				break;
			case 3:
				item?.members.forEach((d) => {
					d.members?.forEach((i) => {
						i.members?.forEach((j) => {
							allMemberData.push(j.group_id);
						})
					});
				});
				break;
		}
		let isAllMemberSelected = true;

		if (selections.length === 0) {
			isAllMemberSelected = false;
		}
		for (const d of allMemberData) {
			if (!selections.includes(d)) {
				isAllMemberSelected = false;
				break;
			}
		}
		return isAllMemberSelected;
	};

	return (
		<li ref={widthRef}>
			<button
				ref={setReferenceElement}
				style={{
					backgroundColor: isHovering ? "#e9edef" : "white",
					fontWeight: isHovering ? "470" : "",
					position: "relative",
					border: "none",
					width: "100%",
					zIndex: "2",
					padding: "16px 8px 16px 16px",
					display: "flex",
					alignContent: "between",
					textAlign: "left",
					justifyContent: "left",
					alignItems: "flex-start",
					borderBottom: "1px solid #e5e3e3",
				}}
				onMouseEnter={() => handleMouseEnter(item.name)}
				onMouseLeave={handleMouseLeave}
				type="button">
				<TvsCheckBox
					id={`branch-dropdown-select-option-${idx + 1}-check-box`}
					handleCheck={(value: number, selected: boolean) => {
						if (item.level === 0) {
							if (selected && !inSelection(value)) {
								setSelections([...selections, value]);
							} else if (!selected) {
								setSelections(removeFromList(value));
							}
						} else {
							if (selected) {
								addMembersToSelected();
							} else {
								removeMembersFromSelected();
							}
						}
					}}
					value={item.group_id}
					selectState={checkSelectState()}
				/>
				<span
					style={{
						fontSize: "16px",
						color: "rgba(40, 50, 55, 1)",
						marginRight: "12px",
					}}>
					{item.name}
				</span>
				{item.members?.length > 0 && (
					<Arrow
						marginTop="8px"
						border={2}
						length={9}
						color="#666666"
						rotate={-45}
					/>
				)}
				{dropdown && (
					<Portal>
						<div
							ref={setPopperElement}
							style={{ ...styles.popper, zIndex: 91 }}
							{...attributes.popper}>
							<Dropdown
								submenus={item}
								selections={selections}
								setSelections={setSelections}
								width={menuWidth}
								parentRef={parentRef}
								direction={direction}
							/>
						</div>
					</Portal>
				)}
			</button>
		</li>
	);
};

const Dropdown = ({
	submenus,
	selections,
	setSelections,
	width,
	direction,
	parentRef,
}: {
	submenus: {
		group_id: number;
		level: number;
		name: string;
		members?: { group_id: number; name: string }[];
	};
	selections: number[];
	setSelections: (val: number[]) => void;
	width: number;
	direction: string;
	parentRef: any;
}) => {
	if (typeof submenus.members == 'undefined') {
		return <></>;
	}

	return (
		<Container
			ref={parentRef}
			style={{
				zIndex: '2',
				listStyle: 'none',
				position: 'absolute',
				flexDirection: 'column',
				padding: '0px',
				maxHeight: '300px',
				overflowY: 'scroll',
				// overflowY: submenus.level < 1 ? 'scroll' : 'visible',
				overflowX: 'hidden',
				backgroundColor: 'white',
				boxShadow:
					'0 4px 6px -1px rgb(0 0 0 / 0.1), 0 4px 6px -1px rgb(0 0 0 / 0.1)',
				// float: 'left',
				left: direction === 'left' ? width / 2 : -(width + width / 2),
				right: direction === 'right' ? width / 2 : -(width + width / 2),
				top: '-60px',
			}}>
			{submenus.members.map(
				(item: { group_id: number; level: number; name: string }, idx: number) => {
					return (
						<MenuItems
							item={item}
							selections={selections}
							setSelections={setSelections}
							idx={idx}
							parentRef={parentRef}
							direction={direction}
						/>
					);
				},
			)}
		</Container>
	);
};

const Container = styled.ul`
	::-webkit-scrollbar-thumb {
		background-color: darkgray;
		border-radius: 100em;
	}
	::-webkit-scrollbar-track {
		background-color: white;
		border-radius: 100em;
	}
	::-webkit-scrollbar {
		width: 5px;
	}
`;
